import * as React from 'react';

import { cn } from '../../../lib/stylingUtils';

type CurrencyCode =
  | 'AED'
  | 'AFN'
  | 'ALL'
  | 'AMD'
  | 'ANG'
  | 'AOA'
  | 'ARS'
  | 'AUD'
  | 'AWG'
  | 'AZN'
  | 'BAM'
  | 'BBD'
  | 'BDT'
  | 'BGN'
  | 'BHD'
  | 'BIF'
  | 'BMD'
  | 'BND'
  | 'BOB'
  | 'BRL'
  | 'BSD'
  | 'BTN'
  | 'BWP'
  | 'BYN'
  | 'BZD'
  | 'CAD'
  | 'CDF'
  | 'CHF'
  | 'CLP'
  | 'CNY'
  | 'COP'
  | 'CRC'
  | 'CUC'
  | 'CUP'
  | 'CVE'
  | 'CZK'
  | 'DJF'
  | 'DKK'
  | 'DOP'
  | 'DZD'
  | 'EGP'
  | 'ERN'
  | 'ETB'
  | 'EUR'
  | 'FJD'
  | 'FKP'
  | 'GBP'
  | 'GEL'
  | 'GHS'
  | 'GIP'
  | 'GMD'
  | 'GNF'
  | 'GTQ'
  | 'GYD'
  | 'HKD'
  | 'HNL'
  | 'HRK'
  | 'HTG'
  | 'HUF'
  | 'IDR'
  | 'ILS'
  | 'INR'
  | 'IQD'
  | 'IRR'
  | 'ISK'
  | 'JMD'
  | 'JOD'
  | 'JPY'
  | 'KES'
  | 'KGS'
  | 'KHR'
  | 'KMF'
  | 'KPW'
  | 'KRW'
  | 'KWD'
  | 'KYD'
  | 'KZT'
  | 'LAK'
  | 'LBP'
  | 'LKR'
  | 'LRD'
  | 'LSL'
  | 'LYD'
  | 'MAD'
  | 'MDL'
  | 'MGA'
  | 'MKD'
  | 'MMK'
  | 'MNT'
  | 'MOP'
  | 'MRU'
  | 'MUR'
  | 'MVR'
  | 'MWK'
  | 'MXN'
  | 'MYR'
  | 'MZN'
  | 'NAD'
  | 'NEUTRAL'
  | 'NGN'
  | 'NIO'
  | 'NOK'
  | 'NPR'
  | 'NZD'
  | 'OMR'
  | 'PAB'
  | 'PEN'
  | 'PGK'
  | 'PHP'
  | 'PKR'
  | 'PLN'
  | 'PYG'
  | 'QAR'
  | 'RON'
  | 'RSD'
  | 'RUB'
  | 'RWF'
  | 'SAR'
  | 'SBD'
  | 'SCR'
  | 'SDG'
  | 'SEK'
  | 'SGD'
  | 'SHP'
  | 'SLL'
  | 'SOS'
  | 'SRD'
  | 'SSP'
  | 'STN'
  | 'SVC'
  | 'SYP'
  | 'SZL'
  | 'THB'
  | 'TJS'
  | 'TMT'
  | 'TND'
  | 'TOP'
  | 'TRY'
  | 'TTD'
  | 'TWD'
  | 'TZS'
  | 'UAH'
  | 'UGX'
  | 'USD'
  | 'UYU'
  | 'UZS'
  | 'VES'
  | 'VND'
  | 'VUV'
  | 'WST'
  | 'XAF'
  | 'XCD'
  | 'XOF'
  | 'XPF'
  | 'YER'
  | 'ZAR'
  | 'ZMW'
  | 'ZWL';

export const Money = ({
  value,
  amount,
  currencyCode = 'USD',
  currency = 'USD',
  omitFractionDigits = false,
  locale = 'en-US',
  className,
}: {
  /** @deprecated */
  currency?: string;
  currencyCode?: CurrencyCode;
  omitFractionDigits?: boolean;
  locale?: string;
  className?: string;
} & (
  | {
      /** @deprecated */
      value?: number;
      amount?: never;
    }
  | {
      amount?: number;
      /** @deprecated */
      value?: never;
    }
)) => {
  const effectiveCurrency = currencyCode ?? currency;
  const effectiveValue = value ?? amount;

  const [signal, currencySymbol, formattedValue] = React.useMemo(
    () =>
      Intl.NumberFormat(locale, {
        style: 'currency',
        currency: effectiveCurrency,
        minimumFractionDigits: omitFractionDigits ? 0 : 2,
        maximumFractionDigits: omitFractionDigits ? 0 : 2,
      })
        .format(effectiveValue ?? 0)
        .match(/^(-)?(.+?)([0-9.,]+)$/)
        ?.slice(1) ?? [],
    [effectiveCurrency, effectiveValue, locale, omitFractionDigits],
  );

  return (
    <div className={cn('@Money', 'contents', className)}>
      <span className="mr-[.25em] font-light opacity-60">{currencySymbol}</span>

      <span>
        {signal && <span className="mr-[.25em] font-light opacity-60">–</span>}
        {formattedValue}
      </span>
    </div>
  );
};
